import { inject, Injectable } from '@angular/core';
import { ApiRouteInspectionService } from '../../../api/elite/routes/inspection.service';
import { forkJoin, Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { Inspection } from '../../../api/elite/types/inspections';
import { ApiRouteUserService } from '../../../api/elite/routes/users.service';
import { User } from '../../../api/elite/types/users';
import { ApiRouteInspectionTestsService } from '../../../api/elite/routes/inspection-tests.service';
import { ApiRouteInspectionVisualService } from '../../../api/elite/routes/inspection-visual.service';
import { InspectionVisualDto } from '../../../api/elite/dto/inspection-visual.dto';
import { ApiRouteInspectionStageService } from '../../../api/elite/routes/inspection-stage.service';
import { InspectionApiDto } from '../../../api/elite/dto/inspection.dto';
import { GoogleFileT } from '../../../api/files/types/google-file';
import { InspectionMeasureTool } from '../../../api/elite/types/inspection-assembly-item';

@Injectable({
  providedIn: 'root',
})
export class QualityControlViewTableApiService {
  protected readonly apiInspectionService = inject(ApiRouteInspectionService);
  protected readonly apiUserRouter = inject(ApiRouteUserService);
  protected readonly apiInspectionTestsService = inject(
    ApiRouteInspectionTestsService
  );
  protected readonly apiVisualService = inject(ApiRouteInspectionVisualService);
  protected readonly apiInspectionStageService = inject(
    ApiRouteInspectionStageService
  );

  getOpenInspection(
    itemIdForInspection: number,
    query: InspectionApiDto['open']['query'] = {}
  ): Observable<Inspection | undefined> {
    return this.apiInspectionService.checkForOpenInspection(
      itemIdForInspection,
      query
    );
  }

  deleteOpenInspection(itemIdForInspection: number): Observable<boolean> {
    return this.getOpenInspection(itemIdForInspection).pipe(
      switchMap((inspection) =>
        this.apiInspectionService.removeInspection(inspection.id)
      )
    );
  }

  getInspectors(departmentName: string): Observable<User[]> {
    return this.apiUserRouter.allUsersV2(departmentName, 'inspector');
  }

  setFinishTests(
    inspectionId: number,
    tests: { id: number; passed: boolean }[]
  ): Observable<any> {
    return this.apiInspectionTestsService.finishTests(inspectionId, tests);
  }

  getDataForVisualInspection(
    inspectionId: number
  ): Observable<InspectionVisualDto['dataForRegisterRecords']['result']> {
    return this.apiVisualService.dataForRegisterRecords(inspectionId);
  }

  getDataForMeasureInspection(
    departmentName: string
  ): Observable<
    InspectionVisualDto['dataForRegisterMeasureRecords']['result'] & {
      measureTools: InspectionMeasureTool[];
    }
  > {
    return forkJoin({
      operators: this.getInspectors(departmentName),
      scrapReasons: of([]),
      measureTools: of([]),
    });
  }

  setStage(
    inspectionId: number,
    title: string
  ): Observable<{ id: number; title: string }> {
    return this.apiInspectionStageService.all().pipe(
      switchMap((res) => {
        const stage = res.find((el) => el.title === title);
        if (!stage) {
          return of(undefined);
        }
        return this.apiInspectionService
          .editInspection(inspectionId, {
            currentStageId: stage.id,
          })
          .pipe(map(() => stage));
      })
    );
  }

  getDrawingFile(item: string): Observable<GoogleFileT> {
    return of(undefined);
  }
}
